import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityLifecyclePicker,
  EntityKindPicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  ScaffolderLayouts,
  ScaffolderFieldExtensions,
} from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
// import { CustomizableHomePage } from './components/home/CustomizableHomePage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  TableProps,
} from '@backstage/core-components';
import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

// import Edit from '@material-ui/icons/Edit';
import Edit from '@material-ui/icons/Edit';
// import { EditEntity } from '@internal/plugin-edit-entities-common';
import { EditEntity } from '@internal/plugin-edit-entities-common';

// Custom Scaffolder
// Layouts
import { TwoColumnLayout } from './scaffolder/layouts/TwoColumnLayouts';
// Fields
import { ValidateNestleOwnersFieldExtension } from './scaffolder/fields/ValidateNestleOwners';
import { IaCApiFilterFieldExtension } from './scaffolder/fields/IaCApiFilter';
import { GithubRepoFilterFieldExtension } from './scaffolder/fields/GithubRepoFilter';
import { ADOProjectPickerFieldExtension } from './scaffolder/fields/ADOProjectPicker';
import { ADORepositoryPickerFieldExtension } from './scaffolder/fields/ADORepositoryPicker';
import { VmwareNetworkPickerFieldExtension } from './scaffolder/fields/VmwareNetworkPicker';
import { GithubUserVerificationFieldExtension } from './scaffolder/fields/GithubUserVerification';
import { GithubTeamPickerFieldExtension } from './scaffolder/fields/GithubTeamPicker';
import { CatalogTeamPickerFieldExtension } from './scaffolder/fields/CatalogTeamPicker';
import { CatalogRepoPickerFieldExtension } from './scaffolder/fields/CatalogRepoPicker';
import { nestleAboutCardPlugin } from '@internal/plugin-react-nestle-about-card';
import { AnsiblePage } from '@internal/plugin-react-ansible-rhaap';
import { GithubCodespacesPage } from '@adityasinghal26/plugin-github-codespaces';
import {
  OnboardingPage,
  TrackPageRoute,
  OnboardingProvider,
  OnboardingOverlay,
  OnboardingTranslationProvider,
  NpsResultsPage,
} from '@internal/plugin-onboarding';
import {
  RecertificationPage,
  RecertifySubmitPage,
  BulkRecertifySubmitPage,
} from '@internal/plugin-react-nestle-recertification';
import { TemplateUsagePage } from './components/templateUsage/TemplateUsagePage';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    // Add binding for Nestle About Card to TechDocs
    bind(nestleAboutCardPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
  },
  components: {
    // SignInPage: props => <SignInPage {...props} auto providers={['guest']} />,
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'local') {
        return <SignInPage {...props} auto providers={['guest']} />;
      }
      return (
        <SignInPage
          {...props}
          auto
          providers={[
            {
              id: 'azure-microsoft-provider',
              title: 'Microsoft',
              message: 'Sign in using Microsoft',
              apiRef: microsoftAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

const customActions: TableProps<CatalogTableRow>['actions'] = [
  ({ entity }) => {
    const isRegionalSystem =
      entity.kind === 'System' &&
      entity.metadata.labels?.['system-type'] === 'regional';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit System',
      hidden: !isRegionalSystem,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
  ({ entity }) => {
    const isCosmosResource =
      entity.kind === 'Resource' && entity.spec?.type === 'cosmosdb-account';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit CosmosDB Account',
      hidden: !isCosmosResource,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
  ({ entity }) => {
    const isMssqlResource =
      entity.kind === 'Resource' && entity.spec?.type === 'mssqlserver';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit MSSQL Server',
      hidden: !isMssqlResource,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
  ({ entity }) => {
    const isPostgresqlResource =
      entity.kind === 'Resource' && entity.spec?.type === 'postgresql';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit PostgreSQL Database',
      hidden: !isPostgresqlResource,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
  ({ entity }) => {
    const isRedisResource =
      entity.kind === 'Resource' &&
      entity.spec?.type === 'redis-cache';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit Redis Cache',
      hidden: !isRedisResource,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
  ({ entity }) => {
    const isEventHubNamespaceResource =
      entity.kind === 'Resource' && entity.spec?.type === 'eventhub-namespace';

    const { editForm, pathname } = EditEntity(entity);

    return {
      icon: () => <Edit fontSize="small" />,
      tooltip: 'Edit EventHub Namespace',
      hidden: !isEventHubNamespaceResource,
      disabled: !editForm,
      onClick: () => {
        if (!editForm) return;
        const search = new URLSearchParams({
          formData: JSON.stringify(editForm),
        }).toString();
        window.location.href = `${pathname('edit')}?${search}`;
      },
    };
  },
];

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
      {/* <CustomizableHomePage /> */}
    </Route>
    <Route path="/onboarding" element={<OnboardingPage />} />
    <Route path="/onboarding/nps-results" element={<NpsResultsPage />} />
    <Route path="/onboarding/:roleId" element={<TrackPageRoute />} />
    <Route
      path="/catalog"
      element={
        // Without `pagination`, EntityListProvider fetches EVERY entity through
        // /entities and filters client-side. At ~4,500 entities that request
        // took 55s. Cursor mode uses /entities/by-query, which paginates and
        // orders in the database.
        <div data-onboarding="catalog-table" style={{ height: '100%' }}>
          <CatalogIndexPage
            actions={customActions}
            pagination={{ mode: 'cursor', limit: 20 }}
            filters={
              <>
                <div data-onboarding="catalog-kind-filter">
                  <EntityKindPicker />
                </div>
                <EntityTypePicker />
                <div data-onboarding="catalog-filters">
                  <UserListPicker />
                  <EntityOwnerPicker />
                  <EntityLifecyclePicker />
                  <EntityTagPicker />
                  <EntityProcessingStatusPicker />
                  <EntityNamespacePicker />
                </div>
              </>
            }
          />
        </div>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid
          config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderLayouts>
        <TwoColumnLayout />
      </ScaffolderLayouts>
      <ScaffolderFieldExtensions>
        <ValidateNestleOwnersFieldExtension />
        <IaCApiFilterFieldExtension />
        <GithubRepoFilterFieldExtension />
        <ADOProjectPickerFieldExtension />
        <ADORepositoryPickerFieldExtension />
        <VmwareNetworkPickerFieldExtension />
        <GithubUserVerificationFieldExtension />
        <GithubTeamPickerFieldExtension />
        <CatalogTeamPickerFieldExtension />
        <CatalogRepoPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/ansible/*" element={<AnsiblePage />} />
    <Route path="/github-codespaces" element={<GithubCodespacesPage />} />
    <Route path="/recertification" element={<RecertificationPage />} />
    <Route
      path="/recertification/bulk/:type"
      element={<BulkRecertifySubmitPage />}
    />
    <Route
      path="/recertification/:type/:resourceId"
      element={<RecertifySubmitPage />}
    />
    <Route path="/template-usage" element={<TemplateUsagePage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <VisitListener />
      <OnboardingTranslationProvider>
        <OnboardingProvider>
          <Root>{routes}</Root>
          <OnboardingOverlay />
        </OnboardingProvider>
      </OnboardingTranslationProvider>
    </AppRouter>
  </>,
);
